import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext(null);
const STORAGE_KEY = 'cf_inquiry_list';

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveToStorage = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore quota errors */ }
};

export function CartProvider({ children }) {
  const [items,     setItems]     = useState(loadFromStorage);
  const [cartOpen,  setCartOpen]  = useState(false);
  const [loading,   setLoading]   = useState(false); // kept for compatibility with existing UI; no longer reflects a real network call

  const itemCount = items.reduce((s, i) => s + i.quantity, 0);

  useEffect(() => { saveToStorage(items); }, [items]);

  // Now takes the full product info directly (no server round-trip to fetch
  // the product name anymore), plus optional productId/sizeId for reference.
  const addToCart = useCallback((productId, sizeId, sizeLabel, quantity = 1, productName = '') => {
    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (i) => i.productId === productId && i.sizeId === sizeId
      );
      if (existingIndex !== -1) {
        const next = [...prev];
        next[existingIndex] = { ...next[existingIndex], quantity: next[existingIndex].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          id: `${productId}-${sizeId || 'nosize'}-${Date.now()}`,
          productId,
          sizeId,
          sizeLabel: sizeLabel || '',
          productName,
          quantity,
        },
      ];
    });
    toast.success('Added to inquiry list!');
    return true;
  }, []);

  const updateItem = useCallback((itemId, quantity) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, quantity } : i)));
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));
    toast.success('Removed from list.');
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Shaped like { items: [...] } so existing code reading `cart.items` still works
  const cart = { items };

  return (
    <CartContext.Provider value={{ cart, cartOpen, setCartOpen, itemCount, loading, addToCart, updateItem, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);